<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const isSubmitting = ref(false);
const deletingCode = ref('');
const hasLoaded = ref(false);
const qrCodes = ref([]);
const form = ref({
  prefilled_message: '',
  image_format: 'PNG',
});

const loadQrCodes = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppQrCodes(props.inbox.id);
    qrCodes.value = response.data?.payload || [];
    hasLoaded.value = true;
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const createQrCode = async () => {
  isSubmitting.value = true;
  try {
    const response = await InboxesAPI.createWhatsAppQrCode(
      props.inbox.id,
      form.value
    );
    qrCodes.value = response.data?.payload || qrCodes.value;
    form.value.prefilled_message = '';
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_CREATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_CREATE_ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const deleteQrCode = async code => {
  deletingCode.value = code;
  try {
    const response = await InboxesAPI.deleteWhatsAppQrCode(props.inbox.id, code);
    qrCodes.value = response.data?.payload || [];
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_DELETE_ERROR')
    );
  } finally {
    deletingCode.value = '';
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) loadQrCodes();
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_NOTE') }}
      </p>
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-ph-arrows-clockwise"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_REFRESH')"
        :is-loading="isLoading"
        @click="loadQrCodes"
      />
    </div>

    <div class="grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4">
      <woot-input
        v-model="form.prefilled_message"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_MESSAGE')"
      />
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        <span class="font-medium">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_FORMAT') }}
        </span>
        <select
          v-model="form.image_format"
          class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option value="PNG">PNG</option>
          <option value="SVG">SVG</option>
        </select>
      </label>
      <div class="flex justify-end">
        <Button
          type="button"
          color="blue"
          icon="i-ph-plus"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_CREATE')"
          :is-loading="isSubmitting"
          @click="createQrCode"
        />
      </div>
    </div>

    <div
      v-if="isLoading"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_LOADING') }}
    </div>

    <div v-else class="grid gap-3">
      <div
        v-for="item in qrCodes"
        :key="item.code || item.id"
        class="rounded-xl border border-n-container bg-n-alpha-2 p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0 flex-1">
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ item.code || item.id }}
            </p>
            <p class="mt-1 mb-0 text-xs text-n-slate-11">
              {{ item.prefilled_message || '—' }}
            </p>
            <div class="mt-3 flex flex-wrap items-center gap-3">
              <img
                v-if="item.qr_image_url"
                :src="item.qr_image_url"
                alt="QR"
                class="h-28 w-28 rounded-lg border border-n-container bg-white object-contain p-2"
              />
              <div class="flex flex-col gap-2 text-xs">
                <a
                  v-if="item.qr_image_url"
                  :href="item.qr_image_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-n-brand hover:underline"
                >
                  {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_OPEN_IMAGE') }}
                </a>
                <a
                  v-if="item.qr_image_url"
                  :href="item.qr_image_url"
                  download
                  class="text-n-brand hover:underline"
                >
                  {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_DOWNLOAD') }}
                </a>
                <a
                  v-if="item.deep_link_url"
                  :href="item.deep_link_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-n-brand hover:underline break-all"
                >
                  {{ item.deep_link_url }}
                </a>
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="faded"
            color="slate"
            icon="i-lucide-trash-2"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_DELETE')"
            :is-loading="deletingCode === (item.code || item.id)"
            @click="deleteQrCode(item.code || item.id)"
          />
        </div>
      </div>

      <div
        v-if="!qrCodes.length && hasLoaded"
        class="rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_EMPTY') }}
      </div>
    </div>
  </div>
</template>
